import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Divider from '@liquidweb-dev/undercurrent/Divider';
import IconButton, {
	IconButtonProps,
} from '@liquidweb-dev/undercurrent/IconButton';
import ArrowUpRightIcon from '@liquidweb-dev/undercurrent/icons/lucide/ArrowUpRight';
import BadgeIcon from '@liquidweb-dev/undercurrent/icons/lucide/Badge';
import CopyIcon from '@liquidweb-dev/undercurrent/icons/lucide/Copy';
import EllipsisIcon from '@liquidweb-dev/undercurrent/icons/lucide/Ellipsis';
import GlobeIcon from '@liquidweb-dev/undercurrent/icons/lucide/Globe';
import HeadsetIcon from '@liquidweb-dev/undercurrent/icons/lucide/Headset';
import PencilIcon from '@liquidweb-dev/undercurrent/icons/lucide/Pencil';
import PowerIcon from '@liquidweb-dev/undercurrent/icons/lucide/Power';
import RefreshCcwIcon from '@liquidweb-dev/undercurrent/icons/lucide/RefreshCcw';
import RotateCcwIcon from '@liquidweb-dev/undercurrent/icons/lucide/RotateCcw';
import TrashIcon from '@liquidweb-dev/undercurrent/icons/lucide/Trash2';
import NutanixIcon from '@liquidweb-dev/undercurrent/icons/platform/Nutanix';
import ListItemIcon from '@liquidweb-dev/undercurrent/ListItemIcon';
import Menu from '@liquidweb-dev/undercurrent/Menu';
import MenuItem from '@liquidweb-dev/undercurrent/MenuItem';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import combineSx from '@liquidweb-dev/undercurrent/utility/combineSx';
import { fromApiBoolean } from '@liquidweb-dev/club-sauce';
import { LWApiPublicAssetDetailsResultRawI } from '@liquidweb-dev/club-sauce/public/asset/index.raw';
import useVmWareVCloudVdcLoginUrl from 'modules/queries/vmWare/vCloud/vdc/useLogin';
import useRebootRequest from 'modules/queries/server/useRebootRequest';
import DialogDestroyServer from 'components/DialogDestroyServer';
import DialogNutanixPrism from 'components/DialogNutanixPrism';
import DialogShutdownServer from 'components/DialogShutdownServer';
import DialogStartServer from 'components/DialogStartServer';
import DrawerCloneServer from 'components/DrawerCloneServer';
import DrawerEditServerHostname from 'components/DrawerEditServerHostname';
import DrawerReboot from 'components/DrawerReboot';
import DrawerReimage from 'components/DrawerReimage';
import DrawerSupportAccess from 'components/DrawerSupportAccess';
import DrawerUnblockIp from 'components/DrawerUnblockIp';

import canReboot from './canReboot';
import isCloudMetal from './isCloudMetal';
import isCloudVps from './isCloudVps';
import FavoriteMenuItem from './FavoriteMenuItem';

type ActionKey =
	| ''
	| 'clone'
	| 'editHostname'
	| 'prism'
	| 'reboot'
	| 'reimage'
	| 'shutdown'
	| 'start'
	| 'supportAccess'
	| 'unblockIp'
	| 'destroy'
	| 'rebootRequest';

const SArrowUpRight = () => (
	<ArrowUpRightIcon
		sx={(theme) => ({ height: theme.size.xS, width: theme.size.xS })}
	/>
);
const SGlobeIcon = () => (
	<GlobeIcon
		sx={(theme) => ({ height: theme.size.xS, width: theme.size.xS })}
	/>
);
const SHeadset = () => (
	<HeadsetIcon
		sx={(theme) => ({ height: theme.size.xS, width: theme.size.xS })}
	/>
);
const SCopy = () => (
	<CopyIcon sx={(theme) => ({ height: theme.size.xS, width: theme.size.xS })} />
);
const SRefreshCcw = () => (
	<RefreshCcwIcon
		sx={(theme) => ({ height: theme.size.xS, width: theme.size.xS })}
	/>
);
const SPencil = () => (
	<PencilIcon
		sx={(theme) => ({ height: theme.size.xS, width: theme.size.xS })}
	/>
);
const SPower = () => (
	<PowerIcon
		sx={(theme) => ({ height: theme.size.xS, width: theme.size.xS })}
	/>
);
const STrash = () => (
	<TrashIcon
		sx={(theme) => ({
			height: theme.size.xS,
			width: theme.size.xS,
			color: theme.vars.palette.uc.danger.icon.main,
		})}
	/>
);

interface ActionsContentProps
	extends Omit<LWApiPublicAssetDetailsResultRawI, 'powerStatus'> {
	intent?: 'serverInterior' | 'serversList';
	isPrismManaged?: boolean;
	isVmware?: boolean;
	isPrivateParent?: boolean;
	iconButtonProps?: IconButtonProps;
	powerStatus?: string;
}
const ActionsContent = ({
	capabilities,
	domain,
	featureDetails,
	ip,
	isPrismManaged,
	isVmware,
	powerStatus,
	status,
	uniq_id: uniqId,
	isPrivateParent,
	scheduledCancellationDate,
	type,
	intent = 'serverInterior',
	iconButtonProps,
}: ActionsContentProps) => {
	const {
		sx: iconButtonSx,
		onClick: iconButtonOnClick,
		...restIconButtonProps
	} = iconButtonProps || {};

	const [actionContentKey, setActionContentKey] = useState<ActionKey>();

	const { mutateAsync: fetchVmWareLogin } = useVmWareVCloudVdcLoginUrl();

	const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLButtonElement | null>();

	const { mutate: requestReboot, isLoading: isRequestRebootLoading } =
		useRebootRequest();

	const isActive = status === 'Active';
	/**
	 * A Prism-managed asset offers Prism Central, Favorite and Destroy Server and
	 * nothing the asset payload reports. The payload is not a safe source here: the
	 * Nutanix team confirmed on 2026-09-17 that powering a single-tenant node off
	 * from the portal risks destroying the cluster even though it reports a power
	 * status, and that a `Nutanix.MT.VPC` should never report one at all.
	 *
	 * `isPrismManaged` covers any Nutanix asset that is not a virtual machine, so an
	 * unrecognized Nutanix container loses these actions instead of gaining them.
	 * The narrower `isNutanixPrivateCloud` drives presentation instead. See
	 * `utility/nutanixProductCodes`.
	 */
	const isCpanel = !isPrismManaged && fromApiBoolean(capabilities?.cpanelLogin);
	const isInterworx =
		!isPrismManaged && fromApiBoolean(capabilities?.interworxLogin);
	const isPlesk = !isPrismManaged && fromApiBoolean(capabilities?.pleskLogin);
	const autoRename = fromApiBoolean(capabilities?.autoRename);
	const manualRename = fromApiBoolean(capabilities?.manualRename);
	const isCancellationScheduled =
		(scheduledCancellationDate?.DateTime || '').length > 0;
	// A Cloud VPS is rebooted directly, so it only gets the request while its
	// power status is unknown and the direct Reboot control is hidden.
	const isDirectRebootOnly = isCloudVps(type) && powerStatus !== 'unknown';
	const isManualReboot =
		!isPrismManaged &&
		!isDirectRebootOnly &&
		fromApiBoolean(capabilities?.manReboot);
	const showRebootAction = !isPrismManaged && canReboot(capabilities);

	const showGoToSupportAccess =
		!isPrismManaged &&
		Boolean(capabilities) &&
		!fromApiBoolean(capabilities?.credentialsNotCustomerFacing);
	const showEditHostname =
		intent === 'serversList' &&
		!isPrismManaged &&
		!isPrivateParent &&
		(autoRename || manualRename);
	const showReimage = !isPrismManaged && fromApiBoolean(capabilities?.reimage);
	const showCloneServer =
		!isPrismManaged && fromApiBoolean(capabilities?.clone);
	const showStartServer =
		!isPrismManaged && !isPrivateParent && isActive && powerStatus === 'off';
	const showShutdownServer =
		!isPrismManaged && !isPrivateParent && isActive && powerStatus === 'on';

	const showDestroyServer =
		intent === 'serversList' && isActive && !isCancellationScheduled;
	const showReboot =
		showRebootAction &&
		intent === 'serversList' &&
		!isPrivateParent &&
		!isCloudMetal(type) &&
		powerStatus !== 'unknown';
	const showCpanelLicense = isCpanel && featureDetails?.cPanelLicenseTier;

	const hasTopGroup =
		showGoToSupportAccess ||
		isCpanel ||
		isInterworx ||
		isPlesk ||
		isPrismManaged ||
		isVmware ||
		showEditHostname;
	const hasBottomGroup =
		showStartServer || showShutdownServer || showDestroyServer;

	const rebootDisabled = !isActive || powerStatus !== 'on';
	const cloneDisabled = !isActive;
	const reimageDisabled = !isActive || powerStatus !== 'on';

	const handleVmWareVCloudVdcLogin = async () => {
		try {
			const data = await fetchVmWareLogin({ uniq_id: uniqId });

			if (data.url) {
				window.open(data.url, '_blank', 'noopener');
			}
		} catch {
			// handled in hook definition
		}
	};

	const handleMenuItemClick = (key: ActionKey) => {
		setMenuAnchorEl(null);
		setActionContentKey(key);
	};

	const handleCloseMenu = () => {
		setMenuAnchorEl(null);
	};

	const handleReboot = () => {
		requestReboot(
			{
				uniq_id: uniqId,
				force: 1,
			},
			{
				onSuccess: () => {
					handleCloseMenu();
				},
			},
		);
	};

	return (
		<>
			<IconButton
				size="large"
				variant="secondary"
				sx={combineSx({ flexGrow: 1 }, iconButtonSx)}
				onClick={(e) => {
					setMenuAnchorEl(e.currentTarget);
					iconButtonOnClick?.(e);
				}}
				{...restIconButtonProps}
			>
				<EllipsisIcon />
			</IconButton>
			<Menu
				open={Boolean(menuAnchorEl)}
				onClose={() => setMenuAnchorEl(null)}
				anchorEl={menuAnchorEl}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'right',
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'right',
				}}
			>
				{isManualReboot && (
					<MenuItem
						disabled={!isActive || isRequestRebootLoading}
						onClick={handleReboot}
					>
						<ListItemIcon>
							<RotateCcwIcon />
						</ListItemIcon>
						<Typography variant="label-lg">Request reboot</Typography>
					</MenuItem>
				)}
				{showGoToSupportAccess && (
					<MenuItem
						disabled={!isActive}
						onClick={() => handleMenuItemClick('supportAccess')}
					>
						<ListItemIcon>
							<SHeadset />
						</ListItemIcon>
						<Typography variant="label-lg">Support access</Typography>
					</MenuItem>
				)}
				{isCpanel && (
					<MenuItem
						disabled={!isActive}
						onClick={() => {
							window.open(`https://${ip}:2083`, '_blank', 'noopener');
						}}
					>
						<ListItemIcon>
							<SArrowUpRight />
						</ListItemIcon>
						<Typography variant="label-lg">Go to cPanel</Typography>
					</MenuItem>
				)}
				{showCpanelLicense && (
					<MenuItem
						component={RouterLink}
						to={`/servers/${uniqId}/cpanel`}
						disabled={!isActive}
					>
						<ListItemIcon>
							<BadgeIcon />
						</ListItemIcon>
						<Typography variant="label-lg">cPanel License Tier</Typography>
					</MenuItem>
				)}
				{isCpanel && (
					<MenuItem
						disabled={!isActive}
						onClick={() => {
							window.open(`https://${ip}:2087`, '_blank', 'noopener');
						}}
					>
						<ListItemIcon>
							<SArrowUpRight />
						</ListItemIcon>
						<Typography variant="label-lg">Go to WHM</Typography>
					</MenuItem>
				)}
				{isCpanel && (
					<MenuItem
						disabled={!isActive}
						onClick={() => handleMenuItemClick('unblockIp')}
					>
						<ListItemIcon>
							<SGlobeIcon />
						</ListItemIcon>
						<Typography variant="label-lg">Unblock IP</Typography>
					</MenuItem>
				)}
				{isInterworx && (
					<MenuItem
						disabled={!isActive}
						onClick={() => {
							window.open(`https://${ip}:2443`, '_blank', 'noopener');
						}}
					>
						<ListItemIcon>
							<SArrowUpRight />
						</ListItemIcon>
						<Typography variant="label-lg">Go to Interworx</Typography>
					</MenuItem>
				)}
				{isPlesk && (
					<MenuItem
						disabled={!isActive}
						onClick={() => {
							window.open(`https://${ip}:8443`, '_blank', 'noopener');
						}}
					>
						<ListItemIcon>
							<SArrowUpRight />
						</ListItemIcon>
						<Typography variant="label-lg">Go to Plesk</Typography>
					</MenuItem>
				)}
				{isVmware && (
					<MenuItem disabled={!isActive} onClick={handleVmWareVCloudVdcLogin}>
						<ListItemIcon>
							<SArrowUpRight />
						</ListItemIcon>
						<Typography variant="label-lg">Go to VMWare</Typography>
					</MenuItem>
				)}
				{isPrismManaged && (
					<MenuItem onClick={() => handleMenuItemClick('prism')}>
						<ListItemIcon>
							<NutanixIcon />
						</ListItemIcon>
						<Typography variant="label-lg">Nutanix Prism Central</Typography>
						<ListItemIcon sx={{ marginLeft: 'auto' }}>
							<ArrowUpRightIcon />
						</ListItemIcon>
					</MenuItem>
				)}
				{showEditHostname && (
					<MenuItem
						disabled={!isActive}
						onClick={() => handleMenuItemClick('editHostname')}
					>
						<ListItemIcon>
							<SPencil />
						</ListItemIcon>
						<Typography variant="label-lg">Edit Hostname</Typography>
					</MenuItem>
				)}
				{hasTopGroup && <Divider sx={{ marginX: { sm: -50 } }} />}

				<FavoriteMenuItem
					onClick={handleCloseMenu}
					favoritesDisplayName={domain}
					uniqId={uniqId}
				/>
				{showCloneServer && (
					<MenuItem
						disabled={cloneDisabled}
						onClick={() => handleMenuItemClick('clone')}
					>
						<ListItemIcon>
							<SCopy />
						</ListItemIcon>
						<Typography variant="label-lg">Clone server</Typography>
					</MenuItem>
				)}
				{showReboot && (
					<MenuItem
						disabled={rebootDisabled}
						onClick={() => handleMenuItemClick('reboot')}
					>
						<ListItemIcon>
							<RotateCcwIcon />
						</ListItemIcon>
						<Typography variant="label-lg">Reboot</Typography>
					</MenuItem>
				)}
				{showReimage && (
					<MenuItem
						disabled={reimageDisabled}
						onClick={() => handleMenuItemClick('reimage')}
					>
						<ListItemIcon>
							<SRefreshCcw />
						</ListItemIcon>
						<Typography variant="label-lg">Reimage</Typography>
					</MenuItem>
				)}
				{hasBottomGroup && <Divider sx={{ marginX: { sm: -50 } }} />}

				{showStartServer && (
					<MenuItem onClick={() => handleMenuItemClick('start')}>
						<ListItemIcon>
							<SPower />
						</ListItemIcon>
						<Typography variant="label-lg">Start server</Typography>
					</MenuItem>
				)}
				{showShutdownServer && (
					<MenuItem onClick={() => handleMenuItemClick('shutdown')}>
						<ListItemIcon>
							<SPower />
						</ListItemIcon>
						<Typography variant="label-lg">Shutdown server</Typography>
					</MenuItem>
				)}
				{showDestroyServer && (
					<MenuItem
						onClick={() => handleMenuItemClick('destroy')}
						sx={{
							':hover, :focus-visible': {
								backgroundColor: ({ vars }) => vars.palette.uc.danger.bg.main,
							},
						}}
					>
						<ListItemIcon>
							<STrash />
						</ListItemIcon>
						<Typography
							variant="label-lg"
							sx={{
								color: 'uc.danger.icon.strong',
							}}
						>
							Destroy server
						</Typography>
					</MenuItem>
				)}
			</Menu>
			<DrawerCloneServer
				open={actionContentKey === 'clone'}
				onClose={() => setActionContentKey('')}
				uniqId={uniqId}
			/>
			<DrawerReboot
				open={actionContentKey === 'reboot'}
				onClose={() => setActionContentKey('')}
				uniqId={uniqId}
			/>
			<DialogShutdownServer
				uniqId={uniqId}
				open={actionContentKey === 'shutdown'}
				onClose={() => setActionContentKey('')}
				domain={domain}
				ip={ip || ''}
			/>
			<DialogStartServer
				uniqId={uniqId}
				open={actionContentKey === 'start'}
				onClose={() => setActionContentKey('')}
				domain={domain}
				ip={ip || ''}
			/>
			<DrawerSupportAccess
				open={actionContentKey === 'supportAccess'}
				onClose={() => setActionContentKey('')}
				uniqId={uniqId}
			/>
			<DrawerEditServerHostname
				open={actionContentKey === 'editHostname'}
				onClose={() => setActionContentKey('')}
				uniqId={uniqId}
			/>
			<DrawerReimage
				open={actionContentKey === 'reimage'}
				onClose={() => setActionContentKey('')}
				uniqId={uniqId}
			/>
			<DrawerUnblockIp
				open={actionContentKey === 'unblockIp'}
				onClose={() => setActionContentKey('')}
				uniqId={uniqId}
			/>
			<DialogNutanixPrism
				open={actionContentKey === 'prism'}
				onClose={() => setActionContentKey('')}
				uniqId={uniqId}
			/>
			<DialogDestroyServer
				open={actionContentKey === 'destroy'}
				onClose={() => setActionContentKey('')}
				uniqId={uniqId}
			/>
		</>
	);
};

export default ActionsContent;
